// src/components/Icon.tsx
import React from 'react';
import * as LucideIcons from 'lucide-react';
import type { LucideProps } from 'lucide-react';

import { ReactComponent as Logo } from 'assets/icons/logo.svg';
import { ReactComponent as OrangeCheckmark } from 'assets/icons/OrganeCheckmark.svg';
import { ReactComponent as Edit } from 'assets/icons/edit-icon.svg';
import { ReactComponent as EditDark } from 'assets/icons/Edit-icon-dark.svg';
import { ReactComponent as RightArrowSmall } from 'assets/icons/right-arrow-small.svg';
import { ReactComponent as GreenCheckmark } from 'assets/icons/GreenCheckmark.svg';
import { ReactComponent as DownRightArrow } from 'assets/icons/DownRightArrow.svg';
import { ReactComponent as OrangeDot } from 'assets/icons/orange-dot.svg';
import { ReactComponent as BlackCircleCheckmark } from 'assets/icons/BlackCircleCheckmark.svg';
import { ReactComponent as BlackCircleX } from 'assets/icons/BlackCircleX.svg';
import { ReactComponent as GreenTrueCheckmark } from 'assets/icons/GreenTrueCheckmark.svg';
import { ReactComponent as RedFalseX } from 'assets/icons/RedFalseX.svg';
import { ReactComponent as GreyX } from 'assets/icons/GreyX.svg';
import { ReactComponent as ArrowCircle } from 'assets/icons/ArrowCircle.svg';
import { ReactComponent as Mic } from 'assets/icons/Mic.svg';
import { ReactComponent as Stop } from 'assets/icons/Stop.svg';
import { ReactComponent as Mountain } from 'assets/icons/Mountain.svg';
import { ReactComponent as Reports } from 'assets/icons/Reports.svg';
import { ReactComponent as Book } from 'assets/icons/Book.svg';
import { ReactComponent as HouseOrange } from 'assets/icons/HouseOrange.svg';
import { ReactComponent as Caution } from 'assets/icons/Caution.svg';
import { ReactComponent as Phone } from 'assets/icons/Phone.svg';
import { ReactComponent as Globe } from 'assets/icons/Globe.svg';
import { ReactComponent as TextBubble } from 'assets/icons/TextBubble.svg';
import { ReactComponent as Play } from 'assets/icons/Play.svg';
import { ReactComponent as Flame } from 'assets/icons/Flame.svg';
import { ReactComponent as Help } from 'assets/icons/Help.svg';
import { ReactComponent as BookDark } from 'assets/icons/BookDark.svg';
import { ReactComponent as Bulb } from 'assets/icons/Bulb.svg';
import { ReactComponent as Coil } from 'assets/icons/Coil.svg';
import { ReactComponent as CoinStacks } from 'assets/icons/CoinStacks.svg';
import { ReactComponent as Dumbell } from 'assets/icons/Dumbell.svg';
import { ReactComponent as Fork } from 'assets/icons/Fork.svg';
import { ReactComponent as Moon } from 'assets/icons/Moon.svg';
import { ReactComponent as Plug } from 'assets/icons/Plug.svg';
import { ReactComponent as Road } from 'assets/icons/Road.svg';
import { ReactComponent as TextBubbles } from 'assets/icons/TextBubbles.svg';
import { ReactComponent as WaterDrop } from 'assets/icons/WaterDrop.svg';
import { ReactComponent as OrangeCircles } from 'assets/icons/OrangeCircles.svg';
import { ReactComponent as OrangeCheckmark2 } from 'assets/icons/OrangeCheckmark2.svg';
import { ReactComponent as Hyperlink } from 'assets/icons/Hyperlink.svg';

const localIconMap = {
  logo: Logo,
  orangeCheckmark: OrangeCheckmark,
  edit: Edit,
  editDark: EditDark,
  rightArrowSmall: RightArrowSmall,
  greenCheckmark: GreenCheckmark,
  downRightArrow: DownRightArrow,
  orangeDot: OrangeDot,
  blackCircleCheckmark: BlackCircleCheckmark,
  blackCircleX: BlackCircleX,
  greenTrueCheckMark: GreenTrueCheckmark,
  redFalseX: RedFalseX,
  greyX: GreyX,
  ArrowCircle: ArrowCircle,
  mic: Mic,
  stop: Stop,
  mountain: Mountain,
  reports: Reports,
  book: Book,
  houseOrange: HouseOrange,
  caution: Caution,
  textBubble: TextBubble,
  globe: Globe,
  phone: Phone,
  play: Play,
  flame: Flame,
  help: Help,
  bookDark: BookDark,
  bulb: Bulb,
  coil: Coil,
  coinStacks: CoinStacks,
  dumbell: Dumbell,
  fork: Fork,
  moon: Moon,
  plug: Plug,
  road: Road,
  textBubbles: TextBubbles,
  waterDrop: WaterDrop,
  orangeCircles: OrangeCircles,
  orangeCheckmark2: OrangeCheckmark2,
  hyperlink: Hyperlink,
} as const;

type LocalIconName = keyof typeof localIconMap;
type LucideIconName = Extract<keyof typeof LucideIcons, string>;

export type IconName = LocalIconName | LucideIconName;

const lucideIconsRecord = LucideIcons as Record<string, unknown>;

type LucideComponent = React.ForwardRefExoticComponent<
  Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
>;

const getLucideIcon = (iconName: string): LucideComponent | null => {
  if (!iconName || iconName === 'Icon') {
    return null;
  }

  const candidate = lucideIconsRecord[iconName];

  if (!candidate) {
    return null;
  }

  if (typeof candidate === 'object' && candidate !== null) {
    const maybeComponent = candidate as { render?: unknown };
    if (typeof maybeComponent.render === 'function') {
      return candidate as LucideComponent;
    }
  }

  return null;
};

const hasLocalIcon = (name: string): name is LocalIconName => name in localIconMap;

interface IconProps extends React.SVGProps<SVGSVGElement> {
  name: IconName;
  size?: number | string; // optional size shortcut
}

export const Icon: React.FC<IconProps> = ({ name, size = 24, ...props }) => {
  if (hasLocalIcon(name)) {
    const LocalIcon = localIconMap[name];
    return <LocalIcon width={size} height={size} {...props} />;
  }

  const LucideIcon = getLucideIcon(name);

  if (LucideIcon) {
    return <LucideIcon size={size} {...props} />;
  }

  if (process.env.NODE_ENV !== 'production') {
    // Surface a warning to help catch typos during development.
    // eslint-disable-next-line no-console
    console.warn(`Icon component: unknown icon "${name}"`);
  }

  return null;
};
